/**
 * 已跟踪草图轮廓重建工具。
 *
 * 文件定位：
 * 1. 该文件位于 web-client/core/sketch 工具层；
 * 2. 负责在草图几何发生变化后，根据稳定标识从“最新轮廓集合”中重建当前特征会话所跟踪的轮廓；
 * 3. 该工具只处理轮廓身份匹配，不直接依赖 Vue、Viewer 或具体 UI 交互。
 */

/**
 * 可参与轮廓重建匹配的最小轮廓接口。
 */
export interface TrackedSketchContourIdentity {
  /** 轮廓主标识。 */
  id: string;
  /** 草图标识。 */
  sketchId: string;
  /** 轮廓源对象标识。 */
  sourceObjectId?: string;
  /** 轮廓稳定键。 */
  contourKey?: string;
}

/**
 * 构建轮廓的稳定匹配键列表。
 *
 * 设计说明：
 * 1. 优先使用 `id` 与 `contourKey`；
 * 2. `sourceObjectId` 作为草图实体来源补充，用于区域对象重建后 `id` 变化但来源仍稳定的场景；
 * 3. 统一带上 `sketchId`，避免跨草图错误匹配。
 *
 * @param contour 轮廓对象。
 * @returns 稳定匹配键列表。
 */
function buildTrackedContourKeys(contour: TrackedSketchContourIdentity): string[] {
  const keys = new Set<string>();
  keys.add(`id:${contour.sketchId}:${contour.id}`);
  if (contour.contourKey) {
    keys.add(`contourKey:${contour.sketchId}:${contour.contourKey}`);
  }
  if (contour.sourceObjectId) {
    keys.add(`sourceObjectId:${contour.sketchId}:${contour.sourceObjectId}`);
  }
  return Array.from(keys);
}

/**
 * 按稳定标识从最新轮廓集合中重建已跟踪轮廓。
 *
 * 设计说明：
 * 1. 输出顺序严格保持当前已跟踪轮廓顺序，避免属性栏列表与预览 target 顺序抖动；
 * 2. 每个最新轮廓最多只消费一次，避免多条已跟踪项错误映射到同一条新轮廓；
 * 3. 无法匹配的轮廓会被丢弃，由上层决定是否清空预览或提示错误。
 *
 * @param trackedContours 当前已跟踪轮廓集合。
 * @param latestContours 最新草图轮廓集合。
 * @returns 按最新几何重建后的轮廓集合。
 */
export function rebuildTrackedSketchContours<T extends TrackedSketchContourIdentity>(
  trackedContours: readonly T[],
  latestContours: readonly T[],
): T[] {
  const latestContourEntries = latestContours.map((contour) => ({
    contour,
    consumed: false,
    keys: buildTrackedContourKeys(contour),
  }));

  return trackedContours.flatMap((trackedContour) => {
    const trackedKeys = buildTrackedContourKeys(trackedContour);
    const matchedEntry = latestContourEntries.find((entry) => (
      !entry.consumed
      && trackedKeys.some((trackedKey) => entry.keys.includes(trackedKey))
    ));
    if (!matchedEntry) {
      return [];
    }
    matchedEntry.consumed = true;
    return [matchedEntry.contour];
  });
}
